/**
 * API error helpers + shape matching backend `ApiErrorResponse` (GlobalExceptionHandler).
 *
 * **Global axios:** `src/app/auth/services/jwtService/jwtService.js` rejects with
 * `normalizeApiError(err)`, so `error.message` is usually already the server `message`
 * for 4xx/5xx with a JSON body. Thunks should still use `getApiErrorMessage` /
 * `getThunkRejectMessage` in catch blocks so behavior is correct if the interceptor
 * is bypassed or for tests.
 */

const DEFAULT_ERROR_MESSAGE = 'Something went wrong. Please try again.';
/**
 * Soft copy for cases where the browser gives Axios no HTTP response
 * (gateway 504/CORS preflight failure, brief outage, timeout, offline).
 * Avoids blaming the user's connection or saying the server is down —
 * impatient clients read that as "your problem" and escalate.
 */
export const NETWORK_RESET_MESSAGE =
  'Session reset in progress. Please try again in a few minutes.';
const AXIOS_STATUS_MESSAGE_REGEX = /^Request failed with status code \d{3}$/i;
const AXIOS_TIMEOUT_MESSAGE_REGEX = /^timeout of \d+ms exceeded$/i;

const isObject = (value) => value !== null && typeof value === 'object' && !Array.isArray(value);

/**
 * True when Axios has no usable response — CORS-blocked preflights, 504 on
 * OPTIONS, offline, and timeouts all look the same to browser JS.
 */
export function isNetworkConnectivityError(error) {
  if (error?.response) {
    return false;
  }

  const code = error?.code;
  const message = typeof error?.message === 'string' ? error.message.trim() : '';

  return (
    code === 'ERR_NETWORK' ||
    code === 'ECONNABORTED' ||
    message === 'Network Error' ||
    message === NETWORK_RESET_MESSAGE ||
    AXIOS_TIMEOUT_MESSAGE_REGEX.test(message)
  );
}

/**
 * True when the API is gone or starting — gateway 502/503/504, TCP drop, timeout.
 * Callers must keep the stored token and retry; logging everyone out causes a
 * login stampede when the process comes back (PGSS-5337).
 */
export function isTransientOutageError(error) {
  if (isNetworkConnectivityError(error)) {
    return true;
  }

  const status = error?.response?.status ?? error?.status;
  return status === 502 || status === 503 || status === 504;
}

/**
 * Auto-login should retry (keep splash + token) for a brief API outage.
 * Must see the original error object — wrapping in `new Error(message)` drops
 * `response.status` and the 503 path never retries (PGSS-5337).
 */
export function shouldRetryAutoLogin(error) {
  return isTransientOutageError(error) || error?.message === NETWORK_RESET_MESSAGE;
}

export function getApiErrorPayload(error) {
  return error?.response?.data;
}

/**
 * `InvalidDataException` — the backend's business-validation failure, raised through
 * `ValidationHelper.handleException` — answers with a bare JSON *array* of
 * `{field, message, rejectedValue}`, not the `ApiErrorResponse` object every other
 * handler returns. Nothing here understood that shape, so those messages were
 * discarded and callers got the generic fallback instead (PGSS-5165: the outbound
 * duplicate-shipment guard names the offending order, and that name never reached
 * the user).
 *
 * One entry per offending value, so all of them are joined rather than reporting the
 * first and hiding the rest. Duplicates are collapsed — the same message can be added
 * once per rejected value.
 */
function getFieldErrorListMessage(payload) {
  if (!Array.isArray(payload)) {
    return '';
  }

  const messages = payload
    .map((entry) => (isObject(entry) && typeof entry.message === 'string' ? entry.message.trim() : ''))
    .filter(Boolean);

  return [...new Set(messages)].join(' ');
}

/**
 * True when body looks like our standard JSON error (message + HTTP status).
 * `errorCode` is optional — some paths may omit it.
 */
export function isApiErrorResponse(payload) {
  return (
    isObject(payload)
    && typeof payload.message === 'string'
    && payload.message.trim() !== ''
    && typeof payload.status === 'number'
  );
}

export function getApiErrorMessage(error, fallbackMessage = DEFAULT_ERROR_MESSAGE) {
  if (isTransientOutageError(error)) {
    // Prefer an explicit caller fallback so feature UIs keep context
    // ("Failed to load board"). Auth paths omit the fallback (or pass the
    // default) so they get the session-reset copy — including when the
    // interceptor already rewrote error.message to NETWORK_RESET_MESSAGE.
    // Must run before payload-string reads: the auth filters always write a body.
    if (fallbackMessage !== DEFAULT_ERROR_MESSAGE) {
      return fallbackMessage;
    }
    return NETWORK_RESET_MESSAGE;
  }

  const payload = getApiErrorPayload(error);

  if (isApiErrorResponse(payload)) {
    return payload.message;
  }

  const fieldErrorListMessage = getFieldErrorListMessage(payload);

  if (fieldErrorListMessage) {
    return fieldErrorListMessage;
  }

  if (typeof payload === 'string' && payload.trim()) {
    return payload;
  }

  if (isObject(payload) && typeof payload.message === 'string' && payload.message.trim()) {
    return payload.message;
  }

  if (typeof error?.message === 'string' && error.message.trim()) {
    if (AXIOS_STATUS_MESSAGE_REGEX.test(error.message.trim())) {
      return fallbackMessage;
    }
    return error.message;
  }

  return fallbackMessage;
}

/**
 * For RTK `createAsyncThunk` catch blocks — pass to `rejectWithValue(...)` so `unwrap()`
 * surfaces a string users can show in toasts.
 * @param {unknown} error Axios error (typically already normalized by JWT interceptor)
 * @returns {string}
 */
export function getThunkRejectMessage(error) {
  return getApiErrorMessage(error, DEFAULT_ERROR_MESSAGE);
}

export function getApiErrorDetails(error) {
  const payload = getApiErrorPayload(error);

  // `InvalidDataException` is the list itself rather than an object wrapping `details`.
  if (Array.isArray(payload)) {
    return payload;
  }

  if (!isObject(payload) || !Array.isArray(payload.details)) {
    return [];
  }

  return payload.details;
}

export function getApiFieldErrors(error) {
  return getApiErrorDetails(error)
    .map((detail) => {
      if (!isObject(detail) || !detail.field) {
        return null;
      }

      return {
        field: detail.field,
        message: detail.defaultMessage || detail.message || 'Invalid value',
      };
    })
    .filter(Boolean);
}

export function normalizeApiError(error) {
  if (!error) {
    return new Error(DEFAULT_ERROR_MESSAGE);
  }

  const normalized = error;
  const payload = getApiErrorPayload(error);

  normalized.message = getApiErrorMessage(error, DEFAULT_ERROR_MESSAGE);

  if (isApiErrorResponse(payload)) {
    normalized.apiError = payload;
    if (typeof payload.errorCode === 'string') {
      normalized.errorCode = payload.errorCode;
    }
    normalized.status = payload.status;
    normalized.path = payload.path;
    normalized.details = Array.isArray(payload.details) ? payload.details : [];
  }

  return normalized;
}
